import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "../../../components/ui/dialog";
import { ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import { toOptions, valueLabel } from "../leadForm";
import { Field, SelectInput } from "./LeadForm";

/** Status colours: the app's shared --status-* tokens, and blue for Cold. */
const BLUE = { bg: "#DBEAFE", fg: "#1D4ED8", dot: "#3B82F6" };
const TONES = {
  Hot: "rejected",
  Warm: "warning",
  Won: "published",
  Partially_Invoiced: "scheduled",
  Fully_Invoiced: "published",
  Lost: "rejected",
};

export function LeadStatusBadge({ status }) {
  const tone = TONES[status] ?? "archived";
  const colors =
    status === "Cold"
      ? BLUE
      : { bg: `var(--status-${tone}-bg)`, fg: `var(--status-${tone}-fg)`, dot: `var(--status-${tone}-dot)` };
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{ backgroundColor: colors.bg, color: colors.fg }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colors.dot }} />
      {valueLabel(status)}
    </span>
  );
}

/**
 * Confirms a status that locks the lead. Lost is the Close Lead screen and
 * needs a lost reason and a note; Fully Invoiced only needs confirming.
 * Rendered with a `key` of the status so each opening starts empty.
 */
export function LeadStatusDialog({ status, lostReasons, busy, error, onCancel, onConfirm }) {
  const { t } = useTranslation();
  const [lostReason, setLostReason] = useState("");
  const [lostNote, setLostNote] = useState("");
  const [touched, setTouched] = useState(false);

  const isLost = status === "Lost";

  const confirm = () => {
    setTouched(true);
    if (!isLost) onConfirm({ leadStatus: status });
    else if (lostReason && lostNote.trim()) onConfirm({ leadStatus: status, lostReason, lostNote: lostNote.trim() });
  };

  const required = (value) => (touched && !value ? t("leadManagement.errorsRequired") : "");

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onCancel()}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] max-w-[460px] sm:w-[460px]",
            "rounded-2xl border border-white/60 bg-white p-6 font-sans shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)]",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <DialogTitle className="mb-3 text-[16px] font-bold text-foreground">
            {isLost ? t("leadManagement.closeTitle") : t("leadManagement.closeConfirmTitle", { status: valueLabel(status) })}
          </DialogTitle>

          <div className="mb-5 rounded-md border-l-4 border-warning bg-amber-50 px-3 py-2.5">
            <p className="m-0 text-sm leading-relaxed text-amber-900">{t("leadManagement.closeLockWarning")}</p>
          </div>

          {isLost ? (
            <div className="mb-5 space-y-4">
              <Field label={t("leadManagement.fieldsLostReason")} htmlFor="lead-lostReason" required error={required(lostReason)}>
                <SelectInput
                  id="lead-lostReason"
                  value={lostReason}
                  options={toOptions(lostReasons)}
                  placeholder={t("leadManagement.placeholdersSelect")}
                  disabled={busy}
                  error={required(lostReason)}
                  onChange={setLostReason}
                />
              </Field>
              <Field label={t("leadManagement.fieldsLostNote")} htmlFor="lead-lostNote" required error={required(lostNote.trim())}>
                <textarea
                  id="lead-lostNote"
                  rows={3}
                  value={lostNote}
                  disabled={busy}
                  onChange={(event) => setLostNote(event.target.value)}
                  className={cn(ui.input, "h-auto w-full py-2")}
                />
              </Field>
            </div>
          ) : null}

          {error ? <p className="mb-3 text-[12px] font-semibold text-rose-600">{error}</p> : null}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="h-10 rounded-xl border border-primary/30 bg-white px-4 text-sm font-semibold text-primary transition hover:bg-workspace/40 disabled:opacity-50"
            >
              {t("formActions.cancel")}
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={busy}
              className={cn(ui.primaryBtn, "h-10 px-4", isLost && "bg-error hover:bg-error/90")}
            >
              {busy ? t("leadManagement.saving") : isLost ? t("leadManagement.closeConfirmLost") : t("leadManagement.closeConfirm")}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
